/**
 * Danger zone section: void, delete, unlink draft.
 * Extracted from InvoiceManager.
 */

import styles from './invoiceManagerStyles';

interface InvoiceRecord {
  status?: string;
  [key: string]: unknown;
}

interface BillRecord {
  id: string;
  [key: string]: unknown;
}

export interface InvoiceDangerZoneProps {
  invoice: InvoiceRecord;
  bills: BillRecord[];
  loading: boolean;
  canUnlinkDraft: boolean;
  formatCurrency: (amount: number) => string;
  onVoid: () => void;
  onDelete: () => void;
  onUnlinkDraft: () => void;
}

export default function InvoiceDangerZone({
  invoice, bills, loading, canUnlinkDraft, formatCurrency: _formatCurrency,
  onVoid, onDelete, onUnlinkDraft,
}: InvoiceDangerZoneProps) {
  return (
    <div style={styles.dangerCard}>
      <h2 style={styles.dangerTitle}>Danger Zone</h2>
      <p style={styles.dangerText}>
        These actions should only be used when absolutely necessary. Consider adjusting the invoice instead.
      </p>

      <div className="grid gap-md" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        <div style={styles.dangerSection}>
          <h3 style={styles.dangerSectionTitle}>Void Invoice</h3>
          <p style={styles.dangerSectionText}>
            Marks the invoice as void in Zoho. Also voids unpaid related bills.
          </p>
          <button
            onClick={onVoid}
            disabled={loading || invoice.status === 'void'}
            style={{ ...styles.dangerBtn, backgroundColor: 'var(--color-warning)', opacity: (loading || invoice.status === 'void') ? 0.5 : 1 }}
          >
            {loading ? 'Voiding...' : 'Void Invoice'}
          </button>
        </div>

        {/* Unlink Draft - only for draft invoices */}
        {invoice.status === 'draft' && canUnlinkDraft && (
          <div style={styles.dangerSection}>
            <h3 style={styles.dangerSectionTitle}>Delete Draft</h3>
            <p style={styles.dangerSectionText}>
              🔗 <strong>Preserve Service Log</strong> — Deletes this draft invoice and all related bills,
              but <strong>keeps the service log</strong> so you can edit and re-approve it.
            </p>
            <button
              onClick={onUnlinkDraft}
              disabled={loading}
              style={{ ...styles.dangerBtn, backgroundColor: 'var(--color-error)' }}
            >
              🗑️ Delete Draft
            </button>
          </div>
        )}

        <div style={styles.dangerSection}>
          <h3 style={styles.dangerSectionTitle}>Delete Invoice</h3>
          {/* Paid invoices cannot be deleted - must remove payments first */}
          {(invoice.status === 'paid' || invoice.status === 'partial') ? (
            <div style={styles.infoBox}>
              <p style={styles.infoText}>
                <strong>💳 This invoice has payments recorded.</strong>
              </p>
              <p style={styles.infoTextSmall}>
                To delete this invoice, you must first remove or credit all payments so the invoice
                returns to an unpaid status. This is consistent with Zoho Books accounting practices.
              </p>
              <p style={styles.infoTextSmall}>
                Go to <strong>Zoho Books → Payments Received</strong> to manage payments.
              </p>
            </div>
          ) : (
            <>
              <p style={styles.dangerSectionText}>
                ⚠️ <strong>PERMANENT ACTION</strong> — This will completely remove the invoice,
                service log, and all {bills.length} related bills from both Supabase and Zoho.
                This cannot be undone!
              </p>
              <button onClick={onDelete} disabled={loading} style={styles.dangerBtn}>
                Delete Invoice Permanently
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
